import { Menu } from '@cobliteam/ui/components';

import { type Media, type MediaEvent, type MediaRequest, MediaType, Severity } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { useCreateMediaShareLink } from '@cobliteam/hooks/src/useCamera/useCameraMedia';

import Icon from '@cobliteam/ui/icons';

import Alert from '@cobliteam/ui/components/Alert';

import { MediaTableType, Months } from '../../utils/mediaUtils';
import { getMediaEventLabel } from '../MediaTags';

interface ShareMediaMenuItemProps {
  media: Media;
  isOnTable?: boolean;
}

const isMobile = () => window.innerWidth <= 800 || window.innerHeight <= 600;

const getSeverityLevelShareLabel = (severity?: Severity) => {
  switch (severity) {
    case Severity.VERY_SEVERE:
      return 'gravíssimo';
    case Severity.SEVERE:
      return 'grave';
    case Severity.MEDIUM:
      return 'médio';
    default:
      return 'médio';
  }
};

const ShareMediaMenuItem = ({ media, isOnTable }: ShareMediaMenuItemProps) => {
  const isEventMedia = media.mediaType === MediaType.EVENT_VIDEO;
  const page = isEventMedia ? MediaTableType.EVENT_MEDIA : MediaTableType.FOOTAGE_MEDIA;

  const { mutateAsync, isError } = useCreateMediaShareLink();

  const generateMessage = async () => {
    const data = await mutateAsync({ mediaId: media.id });
    if (isError) Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
    if (data) {
      const mediaLink = data?.sharableMediaUrl;
      const mediaShareDateObj = new Date(data?.expirationTimestamp);
      const expirationDate = `${mediaShareDateObj.getDate()} de ${Months[mediaShareDateObj.getMonth()]} de ${mediaShareDateObj.getFullYear()}`;

      const eventDateObj = new Date(media.timestamp);
      const mediaDate = `${eventDateObj.getDate()} de ${Months[eventDateObj.getMonth()]} de ${eventDateObj.getFullYear()}`;

      const eventTrigger = (media.mediaDetails as MediaEvent).eventTrigger;
      const eventSeverity = media.severity;
      const requestedBy = (media.mediaDetails as MediaRequest).createdBy?.fullName;
      const driverName = media.driver?.name;
      const mediaRequestDateObj = new Date((media.mediaDetails as MediaRequest).createdAt);
      const mediaRequestDate = `${mediaRequestDateObj.getDate()} de ${Months[mediaRequestDateObj.getMonth()]} de ${mediaRequestDateObj.getFullYear()}`;

      const messageContent = isEventMedia
        ? `Olá, estou compartilhando com você um vídeo de ${getMediaEventLabel(eventTrigger).toLowerCase()} de nível ${getSeverityLevelShareLabel(
            eventSeverity
          )} do dia ${mediaDate}. Para assistir, acesse o link: ${mediaLink} Válido até: ${expirationDate}`
        : `Olá, estou compartilhando com você um vídeo solicitado ${requestedBy ? ` por ${requestedBy}` : ''} no dia ${mediaRequestDate}, referente ao dia ${mediaDate}${
            driverName ? ` do motorista ${driverName}.` : '.'
          } Para assistir, acesse o link: ${mediaLink} Válido até: ${expirationDate}`;

      return messageContent;
    }
  };

  const generateWhatsappLink = (message: string) => {
    const encodedLink = encodeURIComponent(message);
    return isMobile() ? `whatsapp://send?text=${encodedLink}` : `https://web.whatsapp.com/send?text=${encodedLink}`;
  };

  const getVideoURL = () => {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const pathname = window.location.pathname;
    return `${protocol}//${host}${pathname}?id=${media.id}`;
  };

  const handleWhatsappClick = async () => {
    try {
      const message = await generateMessage();
      if (message) {
        const url = generateWhatsappLink(message);
        Analytics.track('Media Share WhatsApp Link Clicked', {
          page,
          isFavorite: media.isFavorite,
          isOnTable: isOnTable,
          videoURL: getVideoURL(),
        });
        window.open(url);
      } else {
        Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
      }
    } catch (error) {
      Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
    }
  };

  const handleCopyLink = async () => {
    try {
      const message = await generateMessage();
      Analytics.track('Event Share Clipboard Link Clicked', {
        page,
        isFavorite: media.isFavorite,
        isOnTable: isOnTable,
        videoURL: getVideoURL(),
      });
      if (message) navigator.clipboard.writeText(message);
      else {
        Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
      }
      Alert.success('Copiado para a área de transferência.');
    } catch (error) {
      Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
    }
  };

  return (
    <>
      <Menu.Item onClick={handleCopyLink} disabled={media.mediaType != MediaType.EVENT_VIDEO && (media.mediaDetails as MediaRequest).status != 'FINISHED'} className='w-full'>
        <Menu.Icon icon={<Icon name='link' />} />
        <Menu.Label>Copiar link do vídeo</Menu.Label>
      </Menu.Item>
      <Menu.Item onClick={handleWhatsappClick} disabled={media.mediaType != MediaType.EVENT_VIDEO && (media.mediaDetails as MediaRequest).status != 'FINISHED'} className='w-full'>
        <Menu.Icon icon={<Icon name='cb-whatsapp' />} />
        <Menu.Label>Enviar por whatsapp</Menu.Label>
      </Menu.Item>
    </>
  );
};

export default ShareMediaMenuItem;
